import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Calendar, ChevronDown, ChevronUp, Info, Utensils, Coffee, Moon, Cookie, Wine, AlertCircle } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import axios from 'axios';
import { toast } from 'sonner';
import { format, parseISO, isToday, startOfDay } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MEAL_TYPE_CONFIG = {
  breakfast: { label: 'Breakfast', icon: Coffee, color: 'from-amber-400 to-orange-500' },
  lunch: { label: 'Lunch', icon: Utensils, color: 'from-green-400 to-emerald-500' },
  dinner: { label: 'Dinner', icon: Moon, color: 'from-indigo-400 to-purple-500' },
  snack: { label: 'Snack', icon: Cookie, color: 'from-pink-400 to-rose-500' },
  drink: { label: 'Drink', icon: Wine, color: 'from-cyan-400 to-blue-500' },
  meal: { label: 'Meal', icon: Utensils, color: 'from-gray-400 to-gray-500' }
};

const FoodSummaryItem = ({ entry, onShowMicronutrients }) => {
  const [showMicronutrients, setShowMicronutrients] = useState(false);
  const config = MEAL_TYPE_CONFIG[entry.meal_type] || MEAL_TYPE_CONFIG.meal;
  const IconComponent = config.icon;

  // Check if micronutrients have been analyzed (uses the micros_analyzed flag set by backend)
  // This flag is set when user clicks "View Micronutrients" in Food Diary
  const hasMicronutrientsData = entry.micros_analyzed === true;

  const handleMicronutrientsClick = () => {
    if (!hasMicronutrientsData) {
      onShowMicronutrients(entry);
    } else {
      setShowMicronutrients(!showMicronutrients);
    }
  };

  // Extract food name from notes or analysis
  const getFoodName = () => {
    if (entry.notes && entry.notes.trim()) {
      return entry.notes;
    }
    if (entry.analysis) {
      const lines = entry.analysis.split('\n');
      for (const line of lines) {
        if (line.trim()) {
          return line.replace(/^\*\*.*?\*\*:?\s*/, '').replace(/^Main:\s*/i, '').trim().substring(0, 100);
        }
      }
    }
    return 'Food item';
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-xl border border-gray-200 p-4 mb-4 shadow-sm"
    >
      {/* Header with meal type */}
      <div className="flex items-center gap-3 mb-3">
        <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${config.color} flex items-center justify-center`}>
          <IconComponent className="w-5 h-5 text-white" />
        </div>
        <div>
          <h3 className="font-bold text-lg text-gray-800">{config.label}</h3>
          <p className="text-gray-600">{getFoodName()}</p>
        </div>
      </div>

      {/* Macronutrients */}
      <div className="grid grid-cols-3 gap-4 mb-4 bg-gray-50 rounded-lg p-3">
        <div className="text-center">
          <p className="text-xs text-gray-500 uppercase tracking-wide">Protein</p>
          <p className="text-lg font-bold text-blue-600">{entry.protein_g?.toFixed(1) || 0}g</p>
        </div>
        <div className="text-center border-x border-gray-200">
          <p className="text-xs text-gray-500 uppercase tracking-wide">Carbs</p>
          <p className="text-lg font-bold text-amber-600">{entry.carbs_g?.toFixed(1) || 0}g</p>
        </div>
        <div className="text-center">
          <p className="text-xs text-gray-500 uppercase tracking-wide">Fat</p>
          <p className="text-lg font-bold text-green-600">{entry.fat_g?.toFixed(1) || 0}g</p>
        </div>
      </div>

      {/* Micronutrients Button */}
      <Button
        variant="outline"
        size="sm"
        onClick={handleMicronutrientsClick}
        className="w-full flex items-center justify-center gap-2"
        data-testid={`micronutrients-btn-${entry.id}`}
      >
        {showMicronutrients ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        Micronutrients
      </Button>

      {/* Micronutrients Panel */}
      <AnimatePresence>
        {showMicronutrients && hasMicronutrientsData && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <div className="mt-4 p-4 bg-gradient-to-br from-purple-50 to-blue-50 rounded-lg border border-purple-100">
              <h4 className="font-semibold text-purple-800 mb-3">Vitamins & Minerals</h4>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Vitamin A</span>
                  <span className="font-medium">{(entry.vitamin_a_mcg || 0).toFixed(1)} mcg</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Vitamin C</span>
                  <span className="font-medium">{(entry.vitamin_c_mg || 0).toFixed(1)} mg</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Vitamin B12</span>
                  <span className="font-medium">{(entry.vitamin_b12_mcg || 0).toFixed(1)} mcg</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Folate</span>
                  <span className="font-medium">{(entry.folate_mcg || 0).toFixed(1)} mcg</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Calcium</span>
                  <span className="font-medium">{(entry.calcium_mg || 0).toFixed(1)} mg</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Iron</span>
                  <span className="font-medium">{(entry.iron_mg || 0).toFixed(1)} mg</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Potassium</span>
                  <span className="font-medium">{(entry.potassium_mg || 0).toFixed(1)} mg</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Sodium</span>
                  <span className="font-medium">{(entry.sodium_mg || 0).toFixed(1)} mg</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Magnesium</span>
                  <span className="font-medium">{(entry.magnesium_mg || 0).toFixed(1)} mg</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Iodine</span>
                  <span className="font-medium">{(entry.iodine_mcg || 0).toFixed(1)} mcg</span>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

const MyFoodSummary = ({ entries }) => {
  const { token } = useAuth();
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [showMicronutrientsAlert, setShowMicronutrientsAlert] = useState(false);
  const [alertEntry, setAlertEntry] = useState(null);

  // Filter entries for selected date
  const filteredEntries = useMemo(() => {
    if (!entries || entries.length === 0) return [];
    
    const selectedDateStr = format(selectedDate, 'yyyy-MM-dd');
    
    return entries.filter(entry => {
      if (!entry.created_at) return false;
      const entryDate = entry.created_at.split('T')[0];
      return entryDate === selectedDateStr;
    }).sort((a, b) => {
      // Sort by meal type order
      const order = ['breakfast', 'lunch', 'snack', 'dinner', 'drink', 'meal'];
      return order.indexOf(a.meal_type) - order.indexOf(b.meal_type);
    });
  }, [entries, selectedDate]);

  // Group entries by meal type
  const groupedEntries = useMemo(() => {
    const groups = {};
    filteredEntries.forEach(entry => {
      const type = entry.meal_type || 'meal';
      if (!groups[type]) groups[type] = [];
      groups[type].push(entry);
    });
    return groups;
  }, [filteredEntries]);

  const handleShowMicronutrientsAlert = (entry) => {
    setAlertEntry(entry);
    setShowMicronutrientsAlert(true);
  };

  const isViewingToday = isToday(selectedDate);

  return (
    <div className="space-y-6">
      {/* Header with Date Picker */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">My Food Summary</h2>
          <p className="text-gray-500 text-sm">
            {isViewingToday ? "Today's meals and nutrition" : format(selectedDate, 'EEEE, MMMM d, yyyy')}
          </p>
        </div>

        <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
          <PopoverTrigger asChild>
            <Button 
              variant="outline" 
              className="flex items-center gap-2"
              data-testid="date-picker-btn"
            >
              <Calendar className="w-4 h-4" />
              {isViewingToday ? 'Today' : format(selectedDate, 'MMM d, yyyy')}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="end">
            <CalendarComponent
              mode="single"
              selected={selectedDate}
              onSelect={(date) => {
                if (date) {
                  setSelectedDate(date);
                  setCalendarOpen(false);
                }
              }}
              disabled={(date) => date > new Date()}
              initialFocus
            />
          </PopoverContent>
        </Popover>
      </div>

      {/* Content */}
      {filteredEntries.length === 0 ? (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center py-16 bg-gray-50 rounded-2xl"
        >
          <Utensils className="w-16 h-16 mx-auto text-gray-300 mb-4" />
          <h3 className="text-xl font-semibold text-gray-600 mb-2">
            {isViewingToday ? "No meals logged today" : "No meals logged for this day"}
          </h3>
          <p className="text-gray-400">
            {isViewingToday 
              ? "Start logging your meals in the Food Diary tab to see your summary here."
              : "Select a different date or log meals for this day."}
          </p>
        </motion.div>
      ) : (
        <div className="space-y-6">
          {Object.entries(groupedEntries).map(([mealType, mealEntries]) => (
            <div key={mealType}>
              {mealEntries.map((entry) => (
                <FoodSummaryItem 
                  key={entry.id} 
                  entry={entry} 
                  onShowMicronutrients={handleShowMicronutrientsAlert}
                />
              ))}
            </div>
          ))}
        </div>
      )}

      {/* Micronutrients Alert Modal */}
      <AnimatePresence>
        {showMicronutrientsAlert && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowMicronutrientsAlert(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl"
              onClick={e => e.stopPropagation()}
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                  <AlertCircle className="w-6 h-6 text-amber-600" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-800 mb-2">Micronutrients Not Available</h3>
                  <p className="text-gray-600 mb-4">
                    To view the micronutrients for this meal, you need to first get the AI analysis in your Food Diary.
                  </p>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                    <p className="text-sm text-blue-800">
                      <strong>How to get micronutrients:</strong>
                    </p>
                    <ol className="text-sm text-blue-700 mt-2 space-y-1 list-decimal list-inside">
                      <li>Go to the <strong>Food Diary</strong> tab</li>
                      <li>Click on the meal image</li>
                      <li>Click the <strong>"View Micronutrients"</strong> button</li>
                      <li>Come back here to see the results</li>
                    </ol>
                  </div>
                  <Button
                    onClick={() => setShowMicronutrientsAlert(false)}
                    className="w-full"
                  >
                    Got it
                  </Button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default MyFoodSummary;
